/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

interface BellProps {
    /**
     * A string representing the CSS class to be applied to the BellIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the BellIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the BellIcon element.
     */
    width?: number | string;
}

/**
 * The `Bell` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `BellProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the BellIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the BellIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the BellIcon element.
 * @returns A React element representing the `Bell` component with the specified properties.
 *
 * @example
 * ```tsx
 * const BellComponent = <Bell className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Bell = ({
    className = '',
    color1 = 'currentColor',
    height = 64,
    ref,
    testId = 'Bell',
    width = 64
}: BellProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 64 64"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M24.3 54c.9 3.4 4 6 7.7 6s6.8-2.6 7.7-6H24.3zm33.3-6.2C54 43 52 37 52 31v-7c0-11-9-20-20-20s-20 9-20 20v7c0 6-2 12-5.6 16.8-.5.6-.5 1.4-.2 2.1.3.7 1 1.1 1.8 1.1h48c.8 0 1.5-.4 1.8-1.1s.3-1.5-.2-2.1z" fill={color1} />
    </svg>
);

Bell.displayName = 'Bell';